import { Form, Input, FormProps, InputNumber, FormItemProps } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import FRadioGroup from 'components/FRadioGroup';
import { VoucherType, RewardType, VoucherDisplayPosition } from 'services/api/voucher';
import { regionMonetaryUnitMap, upperCaseRegionRegionMap } from 'services/config/region';
import dayjs, { Dayjs } from 'lib/dayjs';
import { useVoucherDetailShow, useVoucherDetailData } from 'hooks/business/voucherDetail';
import { inputPosInt, inputPosDecimal, limitPrecision } from 'utils/inputCheck';
import FormWrap from './styled/FormWrap';
import AddProduct from '../ProductSelector/AddProduct';
import useVoucherValues from './useVoucherValues';
import CacheTips from './CacheTips';
import { hasValue } from './utils';
import { DatePicker } from 'components/DatePicker';
import { getCouponTypeLabel } from 'services/i18n/enum/coupon';

const { RangePicker } = DatePicker;
// const MIN_DISCOUNT = 1;
// const MAX_DISCOUNT = 69;
export interface LazadaVoucherFormProps extends FormProps {
  storeName?: string;
  storeId: string;
  region: General.UpperCaseRegion;
  status?: Conversation.Voucher.Status;
  dynamicRules?: Record<string, FormItemProps['rules']>;
  disableCache?: boolean;
}

const FormRegionConfig: Record<
  string,
  {
    /** 金额递增步长 */
    step: number;
    /** 折扣金额 最小 */
    minDiscountAmount: number;
    /** 折扣金额 最大 */
    maxDiscountAmount: number;
    /** 折扣百分比 最小 */
    minDiscount: number;
    /** 折扣百分比 最大 */
    maxDiscount: number;
    /** 最低消费金额 最小 */
    minMinBasketPrice: number;
    /** 最低消费金额 最大 */
    maxMinBasketPrice: number;
    /** 最高优惠金额 最小 */
    minMaxPrice: number;
    /** 最高优惠金额 最大 */
    maxMaxPrice: number;
  }
> = {
  VN: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 2147483647,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 1,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 1,
    maxMaxPrice: 2147483647,
  },
  ID: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 9999999999,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 3000,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 30,
    maxMaxPrice: 9999999999,
  },
  TH: {
    step: 0.01,
    minDiscountAmount: 0.01,
    maxDiscountAmount: 2147483647,
    minDiscount: 1,
    maxDiscount: 69,
    minMinBasketPrice: 1,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 0.02,
    maxMaxPrice: 20000,
  },
  MY: {
    step: 0.01,
    minDiscountAmount: 0.01,
    maxDiscountAmount: 1500,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 0.01,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 0.02,
    maxMaxPrice: 1500,
  },
  SG: {
    step: 0.01,
    minDiscountAmount: 0.01,
    maxDiscountAmount: 2147483647,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 0.01,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 0.02,
    maxMaxPrice: 2147483647,
  },
  PH: {
    step: 0.01,
    minDiscountAmount: 0.01,
    maxDiscountAmount: 100000,
    minDiscount: 1,
    maxDiscount: 99,
    minMinBasketPrice: 0.01,
    maxMinBasketPrice: 9999999999,
    minMaxPrice: 0.02,
    maxMaxPrice: 100000,
  },
};

const isDisabled = (fieldName: string, status?: Conversation.Voucher.Status) => {
  const formDisabledStatus: { [key: string]: Conversation.Voucher.Status[] } = {
    voucherType: ['SCHEDULED', 'ONGOING'],
    voucherName: ['ONGOING'],
    times: ['ONGOING'],
    displayCollectTime: ['SCHEDULED', 'ONGOING'],
    rewardType: ['ONGOING'],
    percentage: ['ONGOING'],
    discountAmount: ['ONGOING'],
    maxPrice: ['ONGOING'],
    minBasketPrice: ['ONGOING'],
    perCustomerLimit: ['ONGOING'],
    itemIdList: [],
    displayChannel: ['SCHEDULED', 'ONGOING', 'ENDED'],
  };

  if (Array.isArray(formDisabledStatus[fieldName]) && status) {
    return formDisabledStatus[fieldName].includes(status);
  }
  return false;
};

const LazadaVoucherForm = (props: LazadaVoucherFormProps) => {
  const initStartTime = dayjs().add(2, 'minutes');
  const initEndTime = dayjs(initStartTime).add(1, 'day');

  const voucherDetailShow = useVoucherDetailShow();

  const voucherDetailData = useVoucherDetailData();

  const {
    status,
    form,
    storeName,
    storeId,
    region,
    initialValues = {
      voucherType: VoucherType.Shop,
      rewardType: RewardType.FixAmount,
      times: [initStartTime, initEndTime],
      usageQuantity: 1,
      perCustomerLimit: 1,
      itemIdList: [],
    },
    dynamicRules = {},
    disableCache,
    ...rest
  } = props;
  const { t } = useTranslate();
  const [type, setType] = useState(VoucherType.Shop);
  const [discount, setDiscount] = useState(RewardType.FixAmount);

  const { [region]: lowRegion } = upperCaseRegionRegionMap;
  const { [lowRegion]: moneyUnit } = regionMonetaryUnitMap;
  const formRegionConfig = FormRegionConfig[region];

  const { clearVoucherFormCache, setVoucherFormCache, getVoucherFormCache } = useVoucherValues();
  const [showCacheTips, setShowCacheTips] = useState(false);
  const [displaySettings, setDisplaySettings] = useState<number | undefined>();

  const typeOptions = useMemo(() => {
    return [
      {
        label: getCouponTypeLabel(t, VoucherType.Shop),
        value: VoucherType.Shop,
      },
      {
        label: getCouponTypeLabel(t, VoucherType.Product),
        value: VoucherType.Product,
      },
    ];
  }, [t]);

  const discountTypeOptions = useMemo(() => {
    return [
      {
        label: t('domain.coupon.discountType.discountPercentage'),
        value: RewardType.DiscountPercentage,
      },
      {
        label: t('domain.coupon.discountType.discountAmount'),
        value: RewardType.FixAmount,
      },
    ];
  }, [t]);

  const displaySettingsOptions = useMemo(() => {
    return [
      {
        label: t('domain.coupon.displaySettings.displayAllPages'),
        value: VoucherDisplayPosition.AllPage,
      },
      {
        label: t('domain.coupon.displaySettings.distributeViaCode'),
        value: undefined,
      },
    ];
  }, [t]);

  useEffect(() => {
    if (!disableCache) {
      // 从缓存读取
      const cached = getVoucherFormCache(storeId);
      if (cached && !status) {
        form?.setFieldsValue(cached);
        setShowCacheTips(true);
      }
    }

    setType(form?.getFieldValue('voucherType'));
    setDiscount(form?.getFieldValue('rewardType'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    if (voucherDetailShow) {
      voucherDetailData?.voucherType && setType(voucherDetailData.voucherType);
      voucherDetailData?.rewardType && setDiscount(voucherDetailData.rewardType);
    }
  }, [voucherDetailData, voucherDetailShow]);

  // 是否是创建表单状态
  const isCreateStatus = useMemo(() => !status, [status]);

  return (
    <Form
      {...rest}
      layout="vertical"
      initialValues={initialValues}
      scrollToFirstError
      form={form}
      onValuesChange={(_, allValues) => {
        if (isCreateStatus) {
          // 新增状态下缓存
          setVoucherFormCache(storeId, allValues);
        }
      }}
    >
      {!disableCache && (
        <CacheTips
          visible={showCacheTips}
          onClick={() => {
            clearVoucherFormCache(storeId);
            form?.resetFields();
            setShowCacheTips(false);
          }}
        />
      )}
      {storeName ? (
        <FormWrap>
          <Form.Item label={t('domain.coupon.field.shopName')} style={{ marginBottom: 0 }}>
            {storeName}
          </Form.Item>
        </FormWrap>
      ) : null}
      <FormWrap title={t('scene.chatWorkspace.couponForm.title.basicInfo')}>
        <Form.Item label={t('domain.coupon.field.type')} name="voucherType" required>
          <FRadioGroup
            options={typeOptions}
            value={type}
            disabled={voucherDetailShow || isDisabled('voucherType', status)}
            onChange={(v) => {
              setType(v as number);
              form?.setFieldsValue({
                voucherType: v,
              });
              if (v === VoucherType.Shop) {
                form?.setFieldsValue({
                  itemIdList: [],
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label={t('domain.coupon.field.name')}
          name="voucherName"
          required
          rules={[
            { required: true, message: t('common.validation.required') },
            ...(dynamicRules.voucherName || []),
          ]}
        >
          <Input
            placeholder={t('scene.chatWorkspace.couponForm.placeholder.couponName')}
            disabled={voucherDetailShow || isDisabled('voucherName', status)}
          />
        </Form.Item>
        <Form.Item
          label={t('domain.coupon.field.validityPeriod')}
          name="times"
          required
          rules={[
            {
              required: true,
              message: t('common.validation.requiredSelect'),
              type: 'array',
            },
            {
              validator: (_, value: [Dayjs, Dayjs]) => {
                if (!isDisabled('times', status) && value) {
                  const [startTime, endTime] = value;
                  if (startTime.isBefore(Date.now())) {
                    return Promise.reject(
                      new Error(t('scene.chatWorkspace.couponForm.validation.startTimeInPast')),
                    );
                  }
                  if (endTime.diff(startTime, 'days') > 180) {
                    return Promise.reject(
                      new Error(
                        t('scene.chatWorkspace.couponForm.validation.durationExceedsMax', {
                          months: 6,
                        }),
                      ),
                    );
                  }
                  if (endTime.diff(startTime, 'minutes') < 10) {
                    return Promise.reject(
                      new Error(
                        t('scene.chatWorkspace.couponForm.validation.durationTooShortMinutes', {
                          minutes: 10,
                        }),
                      ),
                    );
                  }
                }
                return Promise.resolve();
              },
              ...(dynamicRules.times || []),
            },
          ]}
        >
          <RangePicker
            showTime
            disabled={voucherDetailShow || isDisabled('times', status)}
            disabledDate={(current) => {
              return current.isSameOrBefore(dayjs(Date.now()));
            }}
          />
        </Form.Item>

        <Form.Item
          label={t('domain.coupon.field.enablePreview')}
          name="displayCollectTime"
          dependencies={['times']}
          rules={[
            {
              validator: (_, value: Dayjs) => {
                if (!isDisabled('displayCollectTime', status) && hasValue(value)) {
                  if (value.isBefore(Date.now())) {
                    return Promise.reject(
                      new Error(t('scene.chatWorkspace.couponForm.validation.startTimeInPast')),
                    );
                  }

                  const [startTime] = form?.getFieldValue('times') as [Dayjs, Dayjs];
                  if (value.isAfter(startTime)) {
                    return Promise.reject(
                      new Error(t('scene.chatWorkspace.couponForm.validation.previewBeforeStart')),
                    );
                  }
                }
                return Promise.resolve();
              },
            },
            ...(dynamicRules.displayCollectTime || []),
          ]}
        >
          <DatePicker
            showTime
            disabled={voucherDetailShow || isDisabled('displayCollectTime', status)}
            disabledDate={(current) => {
              const [startTime] = form?.getFieldValue('times') as [Dayjs, Dayjs];
              return current.isAfter(startTime);
            }}
          />
        </Form.Item>
      </FormWrap>
      <FormWrap title={t('scene.chatWorkspace.couponForm.title.discountSettings')}>
        <Form.Item label={t('domain.coupon.field.discountType')} name="rewardType" required>
          <FRadioGroup
            value={discount}
            options={discountTypeOptions}
            disabled={status === 'ONGOING'}
            onChange={(v) => {
              setDiscount(v as number);
              form?.setFieldsValue({
                rewardType: v,
              });
              setTimeout(
                () =>
                  form?.validateFields([
                    'percentage',
                    'discountAmount',
                    'maxPrice',
                    'minBasketPrice',
                  ]),
                0,
              );
            }}
          />
        </Form.Item>

        {discount === RewardType.DiscountPercentage ? (
          <Form.Item
            label={t('domain.coupon.field.discountPercentage')}
            name="percentage"
            required
            rules={[
              { required: true, message: t('common.validation.required') },
              {
                validator: (_, value) => {
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ percentage: v.precisionVal });
                    }
                  }

                  if (!isDisabled('percentage', status) && hasValue(value)) {
                    if (value < formRegionConfig.minDiscount) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.discountPercentageMin', {
                            discountPercentage: formRegionConfig.minDiscount,
                          }),
                        ),
                      );
                    }
                    if (value > formRegionConfig.maxDiscount) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.discountPercentageMax', {
                            discountPercentage: formRegionConfig.maxDiscount,
                          }),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
              ...(dynamicRules.percentage || []),
            ]}
          >
            <InputNumber
              addonAfter="%"
              disabled={voucherDetailShow || isDisabled('percentage', status)}
              placeholder={t('common.placeholder.input')}
              min={formRegionConfig.minDiscount}
              max={formRegionConfig.maxDiscount}
              precision={0}
              step={1}
              onKeyPress={inputPosInt}
            />
          </Form.Item>
        ) : null}
        {discount === RewardType.FixAmount ? (
          <Form.Item
            label={t('domain.coupon.field.discountAmount')}
            name="discountAmount"
            dependencies={['minBasketPrice']}
            required
            rules={[
              { required: true, message: t('common.validation.required') },
              {
                validator: (_, value) => {
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ discountAmount: v.precisionVal });
                    }
                  }
                  if (!isDisabled('discountAmount', status)) {
                    const minBasketPrice = form?.getFieldValue('minBasketPrice');
                    if (hasValue(value) && hasValue(minBasketPrice) && value > minBasketPrice) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.discountExceedsMinSpend'),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
              ...(dynamicRules.discountAmount || []),
            ]}
          >
            <InputNumber
              addonAfter={moneyUnit}
              disabled={voucherDetailShow || isDisabled('discountAmount', status)}
              placeholder={t('common.placeholder.input')}
              min={formRegionConfig.minDiscountAmount}
              max={formRegionConfig.maxDiscountAmount}
              precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
              step={formRegionConfig.step}
              onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
            />
          </Form.Item>
        ) : null}
        {discount === RewardType.DiscountPercentage ? (
          <Form.Item
            label={t('domain.coupon.field.maxDiscount')}
            name="maxPrice"
            required
            dependencies={['percentage', 'minBasketPrice']}
            rules={[
              { required: true, message: t('common.validation.required') },
              {
                validator: (_, value) => {
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ maxPrice: v.precisionVal });
                    }
                  }
                  if (!isDisabled('maxPrice', status) && hasValue(value)) {
                    const percentage = form?.getFieldValue('percentage');
                    const minBasketPrice = form?.getFieldValue('minBasketPrice');
                    // 最高优惠金额>=最低消费金额x折扣%
                    if (
                      percentage &&
                      minBasketPrice &&
                      value < (minBasketPrice * percentage) / 100
                    ) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scene.chatWorkspace.couponForm.validation.maxDiscountLessThanCalculated',
                            {
                              discountPercentage: percentage,
                            },
                          ),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
              ...(dynamicRules.maxPrice || []),
            ]}
          >
            <InputNumber
              placeholder={t('common.placeholder.input')}
              addonAfter={moneyUnit}
              disabled={voucherDetailShow || isDisabled('maxPrice', status)}
              min={formRegionConfig.minMaxPrice}
              max={formRegionConfig.maxMaxPrice}
              precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
              step={formRegionConfig.step}
              onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          label={t('domain.coupon.field.minSpend')}
          name="minBasketPrice"
          dependencies={['discountAmount']}
          required
          rules={[
            { required: true, message: t('common.validation.required') },
            {
              validator: (_, value) => {
                if (formRegionConfig.step < 1) {
                  const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                  if (!v.precisionMatch) {
                    form?.setFieldsValue({ minBasketPrice: v.precisionVal });
                  }
                }
                if (!isDisabled('minBasketPrice', status)) {
                  if (discount === 1) {
                    const discountAmount = form?.getFieldValue('discountAmount');
                    if (hasValue(value) && hasValue(discountAmount) && discountAmount > value) {
                      return Promise.reject(
                        new Error(
                          t('scene.chatWorkspace.couponForm.validation.discountExceedsMinSpend'),
                        ),
                      );
                    }
                  }
                }
                return Promise.resolve();
              },
            },
            ...(dynamicRules.minBasketPrice || []),
          ]}
        >
          <InputNumber
            placeholder={t('common.placeholder.input')}
            addonAfter={moneyUnit}
            disabled={voucherDetailShow || isDisabled('minBasketPrice', status)}
            min={formRegionConfig.minMinBasketPrice}
            max={formRegionConfig.maxMinBasketPrice}
            precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
            step={formRegionConfig.step}
            onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
          />
        </Form.Item>

        <Form.Item
          label={t('domain.coupon.field.totalQuantity')}
          name="usageQuantity"
          required
          rules={[
            {
              required: true,
              message: t('common.validation.required'),
            },
            ...(dynamicRules.usageQuantity || []),
          ]}
        >
          <InputNumber
            disabled={voucherDetailShow}
            placeholder={t('common.placeholder.input')}
            min={1}
            max={99999999}
            precision={0}
            step={1}
            onKeyPress={inputPosInt}
          />
        </Form.Item>
        <Form.Item
          label={t('domain.coupon.field.usageLimitPerUser')}
          name="perCustomerLimit"
          required
          dependencies={['usageQuantity']}
          rules={[
            { required: true, message: t('common.validation.required') },
            {
              validator: (_, value) => {
                const usageQuantity = form?.getFieldValue('usageQuantity');
                if (
                  !isDisabled('perCustomerLimit', status) &&
                  hasValue(value) &&
                  hasValue(usageQuantity) &&
                  value > usageQuantity
                ) {
                  return Promise.reject(
                    new Error(
                      t('scene.chatWorkspace.couponForm.validation.limitPerUserExceedsTotal'),
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
            ...(dynamicRules.perCustomerLimit || []),
          ]}
        >
          <InputNumber
            min={1}
            max={50}
            precision={0}
            step={1}
            disabled={voucherDetailShow || isDisabled('perCustomerLimit', status)}
            placeholder={t('common.placeholder.input')}
            onKeyPress={inputPosInt}
          />
        </Form.Item>
      </FormWrap>
      <FormWrap title={t('scene.chatWorkspace.couponForm.title.otherSettings')}>
        <Form.Item label={t('domain.coupon.field.displaySettings')} name="displayChannel" required>
          <FRadioGroup
            value={displaySettings}
            disabled={isDisabled('displayChannel', status)}
            options={displaySettingsOptions}
            onChange={(v) => {
              setDisplaySettings(v as number);
              if (v === -1) {
                form?.setFieldsValue({
                  displayChannel: undefined,
                });
              } else {
                form?.setFieldsValue({
                  displayChannel: v,
                });
              }
            }}
          />
        </Form.Item>
        {type === VoucherType.Product && storeId ? (
          <Form.Item
            label={t('domain.coupon.field.eligibleProducts')}
            name="itemIdList"
            required
            rules={[
              {
                required: true,
                message: t('common.validation.requiredSelect'),
                type: 'array',
              },
              ...(dynamicRules.itemIdList || []),
            ]}
          >
            <AddProduct
              storeId={storeId}
              disabled={voucherDetailShow || isDisabled('itemIdList', status)}
            />
          </Form.Item>
        ) : null}
      </FormWrap>
    </Form>
  );
};

export default LazadaVoucherForm;
